// @flow
import { List } from "immutable/dist/immutable";
import {
    Edge,
    jsToEdgeRecord,
    PagedRequest,
    PageInfo
} from "../records/request.record";
import { jsToCorrectionRecord, jsToPostRecord } from "../records/post.record";

export function setupRequest(propertyName: string) {
    function set(state, obj) {
        return state.set(propertyName, state[propertyName].merge(obj));
    }
    return {
        success: (state: any) => {
            return set(state, {
                requesting: false
            });
        },
        start: (state: any) => {
            return set(state, {
                requesting: true,
                errors: List()
            });
        },
        failure: (
            state: any,
            { payload: { errors } }: { payload: { errors: List<String> } }
        ) => {
            return set(state, {
                requesting: false,
                errors: errors
            });
        }
    };
}

export function mergeEdges(
    edges: List<Edge<*>>,
    pageInfo: PageInfo,
    newEdges: List<Edge<*>>
) {
    let idx = -1;
    if (pageInfo.endCursor) {
        idx = edges.findIndex(({ cursor }) => pageInfo.endCursor === cursor);
    }
    if (idx !== -1) {
        edges = edges
            .take(idx)
            .concat(newEdges)
            .concat(edges.takeLast(edges.count() - idx));
    } else {
        edges = edges.concat(newEdges);
    }

    const ids = {};
    let edgesToReturn = List();
    edges.forEach(edge => {
        const id = edge.node.id;
        if (!ids[id]) {
            edgesToReturn = edgesToReturn.push(edge);
            ids[edge.node.id] = true;
        }
    });
    return edgesToReturn;
}

export function setupCorrectionPagedRequest(name: string) {
    const request = setupPagedRequest(
        name,
        "corrections",
        jsToCorrectionRecord
    );
    return {
        ...request,
        unshiftCorrection(
            state: any,
            { payload: { correction } }: { payload: { correction: any } }
        ) {
            return request.unshift(state, correction);
        },
        updateCorrection(
            state: any,
            { payload: { correction } }: { payload: { correction: any } }
        ) {
            return request.replace(state, correction);
        }
    };
}

export function setupPostPagedRequest(name: string) {
    const request = setupPagedRequest(name, "posts", jsToPostRecord);
    return {
        ...request,
        deleteCorrection(
            state: any,
            { payload: { correction } }: { payload: { correction: any } }
        ) {
            return request.map(state, node => {
                if (correction.postId === node.id) {
                    return node.set(
                        "corrections",
                        node.corrections.filter(c => c.id !== correction.id)
                    );
                }
                return node;
            });
        },
        deletePost(
            state: any,
            { payload: { post } }: { payload: { post: any } }
        ) {
            return request.filter(state, node => node.id !== post.id);
        },
        updatePost(
            state: any,
            { payload: { post } }: { payload: { post: any } }
        ) {
            return request.replace(state, post);
        },
        updateCorrection(
            state: any,
            { payload: { correction } }: { payload: { correction: any } }
        ) {
            return request.map(state, node => {
                if (node.id === correction.postId) {
                    let foundCorrection = false;
                    let corrections = node.corrections.map(c => {
                        if (correction.id === c.id) {
                            foundCorrection = true;
                            return correction;
                        }
                        return c;
                    });

                    if (!foundCorrection) {
                        corrections = corrections.push(correction);
                    }

                    return node.set("corrections", corrections);
                }
                return node;
            });
        }
    };
}

export function setupPagedRequest(
    name: string,
    queryName: string,
    conversionFunc: (t: any) => any
) {
    const request = setupRequest("request");
    const jsToEdge = jsToEdgeRecord(conversionFunc);

    const stateMap = (state: any, func: (t: any) => any) => {
        const pagedRequest = state[name];
        return state.set(
            name,
            pagedRequest.merge({
                edges: pagedRequest.edges.map(edge => {
                    return edge.set("node", func(edge.node));
                })
            })
        );
    };

    const stateFilter = (state: any, pred: (t: any) => boolean) => {
        const pagedRequest = state[name];
        return state.set(
            name,
            pagedRequest.merge({
                edges: pagedRequest.edges.filter(edge => {
                    return pred(edge.node);
                })
            })
        );
    };

    return {
        replace: (state: any, newValue: any) => {
            return stateMap(state, node => {
                if (node.id === newValue.id) {
                    return newValue;
                }
                return node;
            });
        },
        unshift: (state: any, newValue: any) => {
            const pagedRequest = state[name];
            return state.set(
                name,
                pagedRequest.set(
                    "edges",
                    pagedRequest.edges.unshift(
                        new Edge({
                            node: newValue,
                            cursor:
                                newValue.lastUpdateDate || newValue.creationDate
                        })
                    )
                )
            );
        },
        filter: stateFilter,
        reset: (state: any) => {
            return state.set(name, new PagedRequest());
        },
        map: stateMap,
        start: (state: any) => {
            return state.set(name, request.start(state[name]));
        },
        failure: (state: any, action: any) => {
            return state.set(name, request.failure(state[name], action));
        },
        success: (state: any, { payload }: { payload: any }) => {
            const { edges, pageInfo, totalCount } = payload[queryName];
            let pagedRequest = state[name];
            pagedRequest = request.success(pagedRequest);

            let newEdges = mergeEdges(
                pagedRequest.edges,
                pagedRequest.pageInfo,
                List(edges.map(jsToEdge))
            );

            let newPagedInfo = new PageInfo(pageInfo);
            return state.set(
                name,
                pagedRequest.merge({
                    totalCount,
                    edges: newEdges,
                    pageInfo: newPagedInfo
                })
            );
        }
    };
}



// WEBPACK FOOTER //
// ./src/reducers/request.reducer.js